import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../supabaseClient';
import LanguageToggle from '../../components/LanguageToggle';
import VocationWorksLogo from '../../components/VocationWorksLogo';

// This page creates a normal, unprivileged Supabase Auth user via supabase.auth.signUp() —
// nothing more. It does NOT grant admin access. Real access requires a row in the `admins`
// table, which has no INSERT policy on purpose (mirrors how "founding" org invites work) —
// it can only be added via direct database access, by a human, after this signup completes.
// There is deliberately no approval/waitlist UI here: this page can't grant access either way.

const NAVY     = '#0D1B2A';
const NAVY_MID = '#132233';
const AMBER    = '#F5A623';
const WHITE    = '#FFFFFF';
const RED      = '#F87171';
const FONT_HEAD = "'Bebas Neue', sans-serif";
const FONT_BODY = "'DM Sans', sans-serif";

const inp = {
  width: '100%', padding: '11px 13px', fontSize: '0.95rem',
  border: '1.5px solid rgba(255,255,255,0.12)', borderRadius: '8px',
  color: WHITE, outline: 'none', backgroundColor: 'rgba(255,255,255,0.06)',
  fontFamily: FONT_BODY,
};
const lbl = { display: 'block', fontSize: '0.82rem', fontWeight: 600, color: 'rgba(255,255,255,0.55)', marginBottom: '6px' };
const fld = { marginBottom: '1.1rem' };

export default function AdminSignup() {
  const { t } = useTranslation();
  const [form, setForm]         = useState({ name: '', email: '', password: '', confirmPassword: '' });
  const [error, setError]       = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone]         = useState(false);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  useEffect(() => {
    const link = document.createElement('link');
    link.rel  = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Bebas+Neue&family=DM+Sans:wght@400;500;600;700&display=swap';
    document.head.appendChild(link);
    const style = document.createElement('style');
    style.textContent = `*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; } body { background: ${NAVY}; } input::placeholder { color: rgba(255,255,255,0.28); }`;
    document.head.appendChild(style);
    return () => { document.head.removeChild(link); document.head.removeChild(style); };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!form.name.trim() || !form.email.trim() || !form.password || !form.confirmPassword) {
      setError(t('admin.signup.allFieldsRequiredError'));
      return;
    }
    if (form.password.length < 8) {
      setError(t('worker.dashboard.settings.passwordTooShort'));
      return;
    }
    if (form.password !== form.confirmPassword) {
      setError(t('worker.dashboard.settings.passwordMismatch'));
      return;
    }

    setSubmitting(true);
    const { error: signUpError } = await supabase.auth.signUp({
      email: form.email.trim(),
      password: form.password,
      options: { data: { full_name: form.name.trim() } },
    });
    setSubmitting(false);

    if (signUpError) {
      setError(signUpError.message);
      return;
    }
    setDone(true);
  };

  if (done) {
    return (
      <div style={{ background: NAVY, minHeight: '100dvh', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: FONT_BODY, padding: '20px' }}>
        <div style={{ position: 'fixed', top: '16px', right: '16px' }}>
          <LanguageToggle />
        </div>
        <div style={{ background: NAVY_MID, borderRadius: '16px', padding: '2.5rem 2rem', width: '100%', maxWidth: '400px', boxShadow: '0 4px 40px rgba(0,0,0,0.4)', textAlign: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', marginBottom: '1.75rem' }}>
            <VocationWorksLogo variant="reversed" size={36} />
          </div>
          <h1 style={{ fontFamily: FONT_HEAD, fontSize: '1.6rem', color: WHITE, letterSpacing: '0.03em', marginBottom: '0.75rem' }}>
            {t('admin.signup.doneHeading')}
          </h1>
          <p style={{ fontSize: '0.88rem', color: 'rgba(255,255,255,0.55)', lineHeight: 1.6, marginBottom: '1.5rem' }}>
            {t('admin.signup.doneBody')}
          </p>
          <Link to="/admin" style={{ fontSize: '0.85rem', fontWeight: 700, color: AMBER, textDecoration: 'none' }}>
            {t('admin.signup.goToSignInLink')}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div style={{ background: NAVY, minHeight: '100dvh', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: FONT_BODY, padding: '20px' }}>
      <div style={{ position: 'fixed', top: '16px', right: '16px' }}>
        <LanguageToggle />
      </div>
      <div style={{ background: NAVY_MID, borderRadius: '16px', padding: '2.5rem 2rem', width: '100%', maxWidth: '400px', boxShadow: '0 4px 40px rgba(0,0,0,0.4)' }}>

        {/* Logo */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', marginBottom: '1.75rem' }}>
          <VocationWorksLogo variant="reversed" size={36} />
        </div>

        <div style={{ textAlign: 'center', marginBottom: '1.75rem' }}>
          <span style={{ fontSize: '0.68rem', fontWeight: 700, color: AMBER, letterSpacing: '0.14em', textTransform: 'uppercase' }}>
            {t('admin.signup.accountLabel')}
          </span>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={fld}>
            <label style={lbl}>{t('employer.signup.step1.fullNameLabel')}</label>
            <input style={inp} placeholder={t('employer.signup.step1.fullNamePlaceholder')} value={form.name} onChange={e => set('name', e.target.value)} />
          </div>

          <div style={fld}>
            <label style={lbl}>{t('admin.signup.emailLabel')}</label>
            <input style={inp} type="email" placeholder="you@vocationworks.io" value={form.email} onChange={e => set('email', e.target.value)} />
          </div>

          <div style={fld}>
            <label style={lbl}>{t('worker.signup.step1.passwordLabel')}</label>
            <input style={inp} type="password" placeholder={t('worker.signup.step1.passwordPlaceholder')} value={form.password} onChange={e => set('password', e.target.value)} />
          </div>

          <div style={fld}>
            <label style={lbl}>{t('worker.signup.step1.confirmPasswordLabel')}</label>
            <input style={inp} type="password" placeholder={t('worker.signup.step1.confirmPasswordPlaceholder')} value={form.confirmPassword} onChange={e => set('confirmPassword', e.target.value)} />
          </div>

          {error && (
            <p style={{ color: RED, fontSize: '0.84rem', fontWeight: 500, marginBottom: '1.1rem' }}>
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={submitting}
            style={{
              width: '100%', background: AMBER, border: 'none', color: NAVY,
              fontWeight: 700, fontSize: '0.95rem', padding: '12px',
              borderRadius: '8px', cursor: submitting ? 'default' : 'pointer', fontFamily: FONT_BODY,
              opacity: submitting ? 0.7 : 1,
            }}
          >
            {submitting ? t('admin.signup.creatingAccountButton') : t('admin.signup.createAccountButton')}
          </button>
        </form>

        <p style={{ marginTop: '1.5rem', textAlign: 'center', fontSize: '0.75rem', color: 'rgba(255,255,255,0.2)' }}>
          {t('admin.signup.alreadyHavePrefix')}<Link to="/admin" style={{ color: 'rgba(255,255,255,0.35)' }}>{t('admin.signup.signInLink')}</Link>
        </p>
      </div>
    </div>
  );
}
